import { getGetApiV1ThreeLetterCodeClientAdminsClientKey } from '@fukuri-admins/apis/client/gen/endpoints';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useState } from 'react';
import { mutate } from 'swr';
import { usePostShippingAddressConfirmation } from './usePostShippingAddressConfirmation';

type Args = {
  /** 企業コード */
  threeLetterCode: string;
};

/** ダイアログに表示するビュー（確認 / 編集） */
type ViewMode = 'confirm' | 'edit';

type IUseShippingConfirmationDialog = (args: Args) => {
  state: {
    /** 確定 API 実行中かどうか */
    isMutating: boolean;
    /** 表示中のビュー */
    viewMode: ViewMode;
  };
  actions: {
    /** 「この内容で確定」ボタン押下時の処理 */
    handleConfirm: () => void;
    /** 「内容を修正」ボタン押下時の処理（編集ビューへ切り替える） */
    handleEdit: () => void;
    /** 編集フォームの「キャンセル」押下時の処理（確認ビューへ戻す） */
    handleEditCancel: () => void;
    /** 編集フォームの保存成功時の処理（確認ビューへ戻す） */
    handleEditSaved: () => void;
  };
};

/**
 * 送付先住所確認ダイアログの Component Hook
 *
 * - 確定 API の実行
 * - 成功時のトースト表示
 * - エラー時のトースト表示
 * - クライアント情報の再取得（mutate）
 * - 確認ビュー / 編集ビューの切り替え
 *
 * 編集フォームの保存 API・トースト・再取得は編集フォーム側の hook が持つ。
 * 保存後にクライアント情報が再取得されるため、確認ビューに戻ると修正後の内容が表示される。
 */
export const useShippingConfirmationDialog: IUseShippingConfirmationDialog = ({
  threeLetterCode,
}) => {
  const { showToast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>('confirm');

  const { trigger, isMutating } = usePostShippingAddressConfirmation(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '送付先住所を確認しました', status: 'success' });
      // クライアント情報を再取得して confirmed_at を最新化
      mutate(getGetApiV1ThreeLetterCodeClientAdminsClientKey(threeLetterCode));
    },
    onError: error => {
      // 409（確認済み）等のエラーをトーストで通知
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        showToast({ message: errors[0], status: 'danger' });
      } else {
        showToast({ message: '確認処理に失敗しました。', status: 'danger' });
      }
      // エラー時も再取得して最新状態を反映
      mutate(getGetApiV1ThreeLetterCodeClientAdminsClientKey(threeLetterCode));
    },
  });

  const handleConfirm = () => {
    trigger();
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleEditCancel = () => {
    setViewMode('confirm');
  };

  const handleEditSaved = () => {
    setViewMode('confirm');
  };

  return {
    state: {
      isMutating,
      viewMode,
    },
    actions: {
      handleConfirm,
      handleEdit,
      handleEditCancel,
      handleEditSaved,
    },
  };
};
